// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { Animated, Easing, Platform } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';

/** How long an item sits fully visible before it begins handing over. */
export const SLOT_HOLD_MS = 3000;
/** Half a hand-over: the outgoing item fades away, then the incoming one fades in. */
export const SLOT_SWAP_MS = 1000;
/** The shared clock's period — a full hold, plus both halves of the hand-over around it. */
export const SLOT_CYCLE_MS = SLOT_HOLD_MS + SLOT_SWAP_MS * 2;

/*
 * One clock for every slot on screen.
 *
 * Each slot cycling on its own timer looked broken: a row's avatar, its score button and its
 * leaderboard entry all drifted apart, so the same player's hazards appeared in three places at
 * three different moments. Deriving the position from a single shared tick makes them turn over
 * together, however many items each slot happens to hold.
 *
 * The interval only runs while something is subscribed, and only slots with more than one item
 * subscribe at all.
 */
let tick = 0;
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  if (!timer) {
    timer = setInterval(() => {
      tick += 1;
      listeners.forEach((notify) => notify());
    }, SLOT_CYCLE_MS);
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer) {
      clearInterval(timer);
      timer = null;
    }
  };
};

// A slot with nothing to cycle never joins the clock, so a leaderboard of players with one hazard
// each does not re-render every three seconds for nothing.
const subscribeNever = () => () => {};
const getTick = () => tick;

type Props = {
  items: ReactNode[];
  /**
   * Style for the wrapper the zoom is applied to. Callers that size or position the slot must pass
   * it here rather than styling the items: the wrapper is what the items measure against, so a
   * child sized in percentages collapses to nothing without it.
   */
  style?: StyleProp<ViewStyle>;
};

/**
 * Shows one thing at a time in a slot too small for several, handing over on the shared clock.
 *
 * Takes nodes rather than image sources because a ghost is a composed avatar, not a picture. The
 * hand-over fades the outgoing item down and shrinks it slightly, then brings the incoming one
 * back the same way, so the slot reads as one thing changing rather than two images swapping.
 */
export function CyclingSlot({ items, style }: Props) {
  const current = useSyncExternalStore(items.length > 1 ? subscribe : subscribeNever, getTick, getTick);
  // One value drives both halves of the hand-over: 1 is fully shown, 0 fully faded out.
  const progress = useRef(new Animated.Value(1)).current;
  // Lags `current` until the fade-out finishes, so the swap happens while the slot is invisible.
  const [shown, setShown] = useState(current);

  useEffect(() => {
    if (shown === current) return undefined;

    const useNativeDriver = Platform.OS !== 'web';
    Animated.timing(progress, {
      toValue: 0,
      duration: SLOT_SWAP_MS,
      easing: Easing.inOut(Easing.cubic),
      useNativeDriver,
    }).start();

    // The swap is driven by this timer rather than the animation's completion callback, so an
    // interrupted or unavailable animation can never strand the slot on a stale item.
    const swap = setTimeout(() => {
      setShown(current);
      Animated.timing(progress, {
        toValue: 1,
        duration: SLOT_SWAP_MS,
        easing: Easing.inOut(Easing.cubic),
        useNativeDriver,
      }).start();
    }, SLOT_SWAP_MS);

    return () => clearTimeout(swap);
  }, [current, shown, progress]);

  if (items.length === 0) return null;
  // Shrinking only part of the way keeps the item recognisable while the opacity does the work.
  const scale = progress.interpolate({ inputRange: [0, 1], outputRange: [0.6, 1] });
  return (
    <Animated.View style={[style, { opacity: progress, transform: [{ scale }] }]}>
      {items[shown % items.length]}
    </Animated.View>
  );
}

// Clears the interval too, not just the count: each test installs a fresh fake clock, and an
// interval left over from the previous one would keep `timer` non-null so no new one is ever armed.
export const __resetSlotClockForTests = () => {
  tick = 0;
  listeners.clear();
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
};
